import { createTemplateAction } from "@/actions/templates";
import { useApiQuery } from "@/api/useApiQuery";
import { PageHeader } from "@/components/PageHeader";
import type {
  SkillListRow,
  TopicListRow,
  TraitListRow,
} from "@shared/dto";
import { TemplateForm } from "./quests/templates/TemplateForm";

const SKILL_STATUS_ACTIVE = "ACTIVE";
const TRAIT_STATUS_ACTIVE = "ACTIVE";

type MetricRow = {
  id: number;
  code: string;
  unit: string;
  agg: string;
  isActive: boolean;
};

type LocaleRow = { code: string; isDefault: boolean };

// A template is created whole with its default-locale wording: title and
// assignment_template are both NOT NULL, so a template row without a
// translation row is a state the schema does not allow.
export const TemplateNewPage = () => {
  const metrics = useApiQuery<MetricRow[]>("/metrics");
  const topics = useApiQuery<TopicListRow[]>("/topics");
  const skills = useApiQuery<SkillListRow[]>("/skills");
  const traits = useApiQuery<TraitListRow[]>("/traits");
  const locales = useApiQuery<LocaleRow[]>("/locales");

  // The form is one whole: rendering it against half-arrived pickers would
  // offer an empty metric list as though none existed.
  const isLoading =
    metrics.isLoading ||
    topics.isLoading ||
    skills.isLoading ||
    traits.isLoading ||
    locales.isLoading;

  if (isLoading) {
    return (
      <>
        <PageHeader kicker="Catalog" title="New quest template" />
        <p>Loading...</p>
      </>
    );
  }

  return (
    <>
      <PageHeader kicker="Catalog" title="New quest template" />
      <TemplateForm
        action={createTemplateAction}
        values={{
          code: "",
          goalType: "THRESHOLD",
          metricId: "",
          verification: "SELF_REPORT",
          topicId: "",
          participation: ["SOLO"],
          setting: "EITHER",
          tags: "",
          cooldownDays: "1",
          contentGrade: "STOCK",
          hasOpenSubject: false,
          title: "",
          assignment: "",
          skillIds: [],
          requiresTraitIds: [],
          grantsTraitIds: [],
        }}
        metricOptions={(metrics.data ?? [])
          .filter((metric) => metric.isActive)
          .map((metric) => ({
            value: String(metric.id),
            label: `${metric.code} (${metric.unit}, ${metric.agg})`,
          }))}
        topics={(topics.data ?? [])
          .filter((topic) => topic.isActive)
          .map((topic) => ({
            id: topic.id,
            code: topic.code,
            label: topic.label,
          }))}
        localeCode={
          (locales.data ?? []).find((locale) => locale.isDefault)?.code ?? "en"
        }
        bindings={[]}
        hasRungs={false}
        skills={(skills.data ?? [])
          .filter((skill) => skill.status === SKILL_STATUS_ACTIVE)
          .map((skill) => ({
            id: skill.id,
            code: skill.code,
            label: skill.label,
            description: skill.description,
          }))}
        // Archived traits are offered nowhere, and nothing is bound yet, so
        // the flag is constant here — it earns its keep on the detail page,
        // where a binding can outlive the trait's retirement.
        traits={(traits.data ?? []).map((trait) => ({
          id: trait.id,
          code: trait.code,
          label: trait.label,
          isArchived: trait.status !== TRAIT_STATUS_ACTIVE,
        }))}
        isWordingIncluded
        submitLabel="Create template"
        pendingLabel="Creating..."
      />
    </>
  );
};
